import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import {
  AddProgramInput,
  ProgramAttributes,
  UpdateProgramInput,
} from '@loyalty/shared-common/gamification-api-client';
import { Alert } from '@mui/material';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useSelectiveEffect } from '../shared/use-selective-effect';

export interface ProgramsContextInterface {
  programs: ProgramAttributes[];
  addProgram: (program: AddProgramInput) => Promise<ProgramAttributes | undefined>;
  getPrograms: () => void;
  updateProgram: (
    id: string,
    program: UpdateProgramInput,
  ) => Promise<ProgramAttributes | undefined>;
  deleteProgram: (program: ProgramAttributes) => void;
  isLoading: IsLoading;
}

export const ProgramsContext = createContext<ProgramsContextInterface>({} as any);

export function usePrograms() {
  return useContext(ProgramsContext);
}

export function ProgramsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { gamificationApi, managerApi } = useApi();
  const { currentAccount } = useAccounts();
  const [error, setError] = useState<any>();
  const [programs, setPrograms] = useState<ProgramAttributes[]>([]);

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const getPrograms = useCallback(() => {
    addLoader('getPrograms');
    gamificationApi
      .getPrograms(currentAccount.id)
      .then(setPrograms)
      .catch(error => {
        notify(t('loyalty.errors.failed-fetch-programs', { error }), 'error');
        setError(error);
      })
      .finally(() => removeLoader('getPrograms'));
  }, [addLoader, currentAccount?.id, gamificationApi, notify, removeLoader, t]);

  const addProgram = useCallback(
    async (program: AddProgramInput) => {
      addLoader('addProgram');
      return managerApi
        .addProgram(currentAccount.id, program)
        .then(program => {
          setPrograms(programs => [...programs, program]);
          return program;
        })
        .catch(error => {
          notify(t('loyalty.errors.failed-add-program', { error }), 'error');
          return undefined;
        })
        .finally(() => removeLoader('addProgram'));
    },
    [addLoader, currentAccount?.id, managerApi, notify, removeLoader, t],
  );

  const updateProgram = useCallback(
    async (id: string, program: UpdateProgramInput) => {
      addLoader('updateProgram');
      return gamificationApi
        .updateProgram(id, program)
        .then(program => {
          setPrograms(programs => programs.map(p => (p.id === program.id ? program : p)));
          return program;
        })
        .catch(error => {
          notify(t('loyalty.errors.failed-update-program', { error }), 'error');
          return undefined;
        })
        .finally(() => removeLoader('updateProgram'));
    },
    [addLoader, gamificationApi, notify, removeLoader, t],
  );

  const deleteProgram = useCallback((program: ProgramAttributes) => {
    setPrograms(programs => programs.filter(p => p.id !== program.id));
  }, []);

  useSelectiveEffect(
    () => {
      if (currentAccount) {
        getPrograms();
      }
    },
    [getPrograms, currentAccount],
    [currentAccount],
  );

  const context = {
    programs,
    addProgram,
    getPrograms,
    updateProgram,
    deleteProgram,
    isLoading,
  };

  return error ? (
    <Alert severity="error">{error.toString()}</Alert>
  ) : (
    <ProgramsContext.Provider value={context}>{children}</ProgramsContext.Provider>
  );
}
